import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { Button as AriaButton } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import type { Workspace } from '@/api/generated/schemas.ts';
import { useListWorkspaces } from '@/api/generated/workspaces.ts';
import { getWorkspacePath, useWorkspace, useWorkspacePath } from '@/api/workspaces.ts';
import Button from '@/core/components/Button.tsx';
import Menu from '@/core/components/Menu.tsx';
import { useDialogParam } from '@/utils/useSearchParam.ts';

import WorkspaceAvatar from './WorkspaceAvatar.tsx';
import WorkspaceDialog from './WorkspaceDialog.tsx';

// Switching keeps the section (documents stay documents), but not a detail page or filters: those belong to the
// workspace that was open. A client's employee only has the home and the recaps (plan/DECISIONS.md D61).
const useSectionPath = () => {
    const { pathname } = useLocation();
    const section = pathname.split('/')[3];
    return (workspace: Workspace) => {
        const isOpen = section && (workspace.role !== 'employee' || section === 'recaps');
        return getWorkspacePath(workspace.slug, isOpen ? `/${section}` : '/');
    };
};

const WorkspaceSwitcher = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const workspace = useWorkspace();
    const workspaces = useListWorkspaces();
    const workspacePath = useWorkspacePath();
    const toSectionPath = useSectionPath();
    const { dialog, openDialog, closeDialog } = useDialogParam('new-workspace');

    return (
        <>
            <Menu.Trigger>
                <Button asChild variant="ghost" size="sm" className="-ml-2 min-w-0 gap-2 px-2 text-text">
                    <AriaButton aria-label={t('workspaces.switch', { name: workspace.name })}>
                        <WorkspaceAvatar workspace={workspace} size="xs" />
                        <span className="truncate max-sm:hidden">{workspace.name}</span>
                        <ChevronsUpDown className="text-text-subtle" />
                    </AriaButton>
                </Button>
                <Menu placement="bottom start" className="min-w-64">
                    {(workspaces.data ?? [])
                        .filter((item) => !item.is_access_disabled)
                        .map((item) => (
                            <Menu.Item
                                key={item.id}
                                id={item.id}
                                href={toSectionPath(item)}
                                textValue={item.name}
                                icon={<WorkspaceAvatar workspace={item} size="xs" />}
                            >
                                <span className="flex-1 truncate">{item.name}</span>
                                <Check className={item.id === workspace.id ? undefined : 'invisible'} />
                            </Menu.Item>
                        ))}
                    <Menu.Separator />
                    <Menu.Item id="new" icon={<Plus />} onAction={() => openDialog('new-workspace')}>
                        {t('workspaces.new')}
                    </Menu.Item>
                </Menu>
            </Menu.Trigger>
            <WorkspaceDialog
                target={dialog === 'new-workspace' ? { workspace: null } : null}
                onClose={closeDialog}
                // The new workspace has no categories yet, so the settings are the next stop.
                onSaved={(saved) => navigate(getWorkspacePath(saved.slug, '/settings'))}
            />
        </>
    );
};

export default WorkspaceSwitcher;
